import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db, auth } from "../firebase";

function ManageEvents() {
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start: "",
    end: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");

  // Subscribe to "events" collection ordered by start time.
  useEffect(() => {
    const q = query(collection(db, "events"), orderBy("start", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        // Convert Firestore Timestamps to JS Date objects if needed:
        const start = data.start?.toDate ? data.start.toDate() : data.start;
        const end = data.end?.toDate ? data.end.toDate() : data.end;
        return {
          id: doc.id,
          title: data.title,
          description: data.description,
          start,
          end,
        };
      });
      setEvents(eventsData);
    });
    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle add/update event.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!auth.currentUser) {
      setMessage("You must be signed in to add an event.");
      return;
    }

    try {
      // Parse start and end from the datetime-local inputs.
      const data = {
        title: formData.title,
        description: formData.description,
        start: new Date(formData.start),
        end: new Date(formData.end),
      };

      if (editingId) {
        // Update existing event.
        const eventRef = doc(db, "events", editingId);
        await updateDoc(eventRef, data);
        setMessage("Event updated successfully!");
      } else {
        // Add new event.
        await addDoc(collection(db, "events"), data);
        setMessage("Event added successfully!");
      }
      // Reset form and editing state.
      setFormData({
        title: "",
        description: "",
        start: "",
        end: "",
      });
      setEditingId(null);
    } catch (error) {
      console.error("Error saving event:", error);
      setMessage("An error occurred while saving the event.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "events", id));
      setMessage("Event deleted successfully!");
    } catch (error) {
      console.error("Error deleting event:", error);
      setMessage("An error occurred while deleting the event.");
    }
  };

  const handleEdit = (eventItem) => {
    setFormData({
      title: eventItem.title,
      description: eventItem.description,
      // Format the date-time string for the native datetime-local input.
      start: new Date(eventItem.start).toISOString().slice(0, 16),
      end: new Date(eventItem.end).toISOString().slice(0, 16),
    });
    setEditingId(eventItem.id);
  };

  return (
    <Container sx={{ my: 4 }}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Manage Events
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={3}
            required
          />
          <TextField
            fullWidth
            label="Start Date & Time"
            name="start"
            type="datetime-local"
            value={formData.start}
            onChange={handleInputChange}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            required
          />
          <TextField
            fullWidth
            label="End Date & Time"
            name="end"
            type="datetime-local"
            value={formData.end}
            onChange={handleInputChange}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            required
          />
          <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }}>
            {editingId ? "Update Event" : "Add Event"}
          </Button>
        </form>
        {message && (
          <Typography variant="subtitle1" color="secondary" sx={{ mt: 2 }}>
            {message}
          </Typography>
        )}

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Existing Events
          </Typography>
          <List>
            {events.map((eventItem) => (
              <ListItem key={eventItem.id} divider>
                <ListItemText
                  primary={eventItem.title}
                  secondary={`Starts: ${new Date(eventItem.start).toLocaleString()} | Ends: ${new Date(
                    eventItem.end
                  ).toLocaleString()}`}
                />
                <IconButton onClick={() => handleEdit(eventItem)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(eventItem.id)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Container>
  );
}

export default ManageEvents;
